import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Environment } from 'dashboard/models/environment';
import { ExternalIntegration } from 'dashboard/models/external-integration';
import { Importer, ImporterRunType, ImporterType } from 'dashboard/models/importer';
import {
  setExternalIntegration,
  setExternalIntegrations,
} from 'dashboard/redux/actions/external-integrations';
import { saveImporters, saveImporterToImporters } from 'dashboard/redux/actions/importers';
import { ApiEndpoint, PaginationParams } from 'lib/interfaces/api';

export function loadImportersList(environment: Environment) {
  return async (dispatch: Dispatch) => {
    const params = {
      filter: {
        'environment-id': environment.id,
      },
    };

    const response = await SessionClientDashboard.listResource<Importer, PaginationParams>(
      ApiEndpoint.ImporterPipeline,
      params,
    );

    dispatch(
      setExternalIntegrations(
        response.included?.filter((o) =>
          o.type.startsWith('external-integration/'),
        ) as ExternalIntegration[],
        true,
      ),
    );
    dispatch(saveImporters(response.data));
    return response;
  };
}

export const createImporter = (
  name: string,
  importerType: ImporterType,
  dataSourceId: string,
  config: { [key: string]: any },
) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceCreate<Importer>(
      {
        type: importerType,
        attributes: { name, 'pipeline-config': config },
      },
      {
        meta: { 'external-integration-id': dataSourceId },
      },
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const updateImporter = (
  importerId: string,
  importerType: ImporterType,
  name: string,
  config: { [key: string]: any },
  dataSourceId = '',
  runType: ImporterRunType = ImporterRunType.Manual,
) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.updateResource<Importer>(
      {
        type: importerType,
        id: importerId,
        attributes: { name, 'pipeline-config': config, 'run-type': runType },
      },
      {
        meta: { 'external-integration-id': dataSourceId },
      },
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const updateImporterName = (
  importerId: string,
  importerType: ImporterType,
  name: string,
) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.updateResource<Importer>({
      type: importerType,
      id: importerId,
      attributes: { name },
    });
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const activateImporter = (importer: Importer) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceAction<Importer>(
      { id: importer.id, type: importer.type },
      'activate',
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const archiveImporter = (importer: Importer) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceAction<Importer>(
      { id: importer.id, type: importer.type },
      'archive',
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const cloneImporter = (importer: Importer, name: string, dataSourceId: string) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceAction<Importer>(
      {
        id: importer.id,
        type: importer.type,
        attributes: { name: name },
      },
      'clone',
      {
        meta: {
          external_integration_token: dataSourceId,
        },
      },
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const deactivateImporter = (importer: Importer) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.postResourceAction<Importer>(
      { id: importer.id, type: importer.type },
      'deactivate',
    );
    dispatch(saveImporterToImporters(response.data));
    return response;
  };
};

export const loadImporter = (importerId: string) => {
  return async (dispatch: Dispatch) => {
    const response = await SessionClientDashboard.getResource<Importer>(
      ApiEndpoint.ImporterPipeline,
      importerId,
    );
    dispatch(saveImporterToImporters(response.data));
    dispatch(
      setExternalIntegration(
        response.included?.find((o) =>
          o.type.startsWith('external-integration/'),
        ) as ExternalIntegration,
      ),
    );
    return Promise.resolve(response.data);
  };
};
